import { Inject, Injectable } from '@angular/core';
import {
    ConcurrentUsageService,
    CONFIRMATION_TYPES,
    ConfirmationModel,
    ConfirmationModelFromJson,
    NotificationIdentifierModel,
} from '@evorait/pace-core';
import { catchError, map, Observable, of } from 'rxjs';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { CONFIRMATION_CONCURRENT_STORE_KEY } from './confirmation.const';

@Injectable({ providedIn: 'root' })
export class ConfirmationConcurrentStoreService {
    public constructor(
        private readonly concurrentUsageService: ConcurrentUsageService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public saveConfirmation(confirmation: ConfirmationModel): Observable<ConfirmationModel> {
        const key = ConfirmationConcurrentStoreService.generateKey(
            confirmation.notificationIdentifier,
            confirmation.type,
        );
        return this.concurrentUsageService
            .writeDataForKey(key, JSON.stringify(confirmation), confirmation.saveTimestamp)
            .pipe(
                map(() => confirmation),
                catchError((error) => {
                    this.log.debug(
                        `[Confirmation Concurrency Store]: could not write to confirmation to concurrency store: ${JSON.stringify(
                            error,
                        )}`,
                    );
                    return of(confirmation);
                }),
            );
    }

    public removeConfirmation(confirmation: ConfirmationModel): Observable<boolean> {
        const key = ConfirmationConcurrentStoreService.generateKey(
            confirmation.notificationIdentifier,
            confirmation.type,
        );
        return this.concurrentUsageService.deleteDataForKey(key).pipe(
            map(() => true),
            catchError((error) => {
                this.log.debug(
                    `[Confirmation Concurrency Store]: removing confirmation model failed: ${JSON.stringify(error)}`,
                );
                return of(false);
            }),
        );
    }

    public getConfirmation(id: NotificationIdentifierModel, type: CONFIRMATION_TYPES): Observable<ConfirmationModel> {
        const key = ConfirmationConcurrentStoreService.generateKey(id, type);
        return this.concurrentUsageService.readDataForKey(key).pipe(
            map((res) => {
                if (res?.length > 0) {
                    const resultConfirmation = new ConfirmationModelFromJson(JSON.parse(res[0].value), type);
                    if (res[0].timestamp !== resultConfirmation.saveTimestamp) {
                        resultConfirmation.saveTimestamp = res[0].timestamp;
                    }
                    return resultConfirmation;
                }
                return new ConfirmationModel(id, type);
            }),
            catchError((error) => {
                this.log.debug(
                    `[Confirmation Concurrency Store]: Creating new confirmation model since it was not possible to load one: ${JSON.stringify(
                        error,
                    )}`,
                );
                const confirmation = new ConfirmationModel(id, type);
                return of(confirmation);
            }),
        );
    }

    private static generateKey(id: NotificationIdentifierModel, type: CONFIRMATION_TYPES): string {
        return `${CONFIRMATION_CONCURRENT_STORE_KEY}-${id.toString()}-${type}`;
    }
}
